/**
 * How far a bar's fill reaches (ADR-061, amendment "the design system lands": bars crop, never scale). A bar
 * draws its fill strip across the whole inside of its track and crops it to a whole number of logical
 * pixels, so the strip's highlight and shade rows and its end columns keep their pixels at any value, and a
 * one-pixel strip is never stretched. The same crop serves a canvas bar, which crops its fill sprite.
 */

/**
 * The width a bar's fill is cropped to: the value's share of the track's inside, rounded down to a whole
 * logical pixel, so a bar reads full only once its value reaches its maximum and a sliver of progress shows
 * nothing until it earns a pixel. A value past either end is held to the track; a value or a maximum that
 * is not a number, and a maximum of zero or less, show no fill.
 *
 * The share is the value divided by the maximum before it is multiplied by the inside, so a value near the
 * largest number never overflows to a full bar, and a value below its maximum is held a pixel short of the
 * whole inside, since the share of a value a rounding step below its maximum can round up to exactly one.
 *
 * @param value How much the bar holds.
 * @param max How much it holds when full.
 * @param inner The width of the track's inside in logical pixels, which the full fill covers.
 * @returns The fill's width in whole logical pixels, from 0 to `inner`.
 * @throws RangeError when `inner` is not a whole number of logical pixels or is negative.
 * @example
 * barFill(99, 100, 150);
 * // 148
 */
export function barFill(value: number, max: number, inner: number): number {
  if (!Number.isInteger(inner) || inner < 0) {
    throw new RangeError(`a bar's inside of ${inner} is not a whole number of logical pixels`);
  }
  if (Number.isNaN(value) || Number.isNaN(max) || max <= 0 || value <= 0) {
    return 0;
  }
  if (value >= max) {
    return inner;
  }
  return Math.min(Math.max(0, inner - 1), Math.floor((value / max) * inner));
}
